import type { AdherenceStatus } from "@/api/diagnostic";
import type { WricefDecisionUpdate } from "@/api/wricef-decisions";

/*
  O "Efeito no índice" da decisão do WRICEF (EF-FUNC-011, RF-02): a cor que o
  objeto ganha e onde ele conta no índice de aderência. É a regra por objeto do
  motor (`_wricef_status` e `_development` em `services/api/app/services/
  adherence_index.py`), repetida aqui porque o painel acompanha o rascunho antes
  de salvar. Mudou lá, muda aqui.
*/

export type AdherenceCountsIn = "developments" | "scope_item" | "excluded";

export interface WricefAdherenceEffect {
  status: AdherenceStatus;
  countsIn: AdherenceCountsIn;
  /** O scope item associado que recebe a cor, ou o que deixou de ser "Ativar". */
  scopeItem: string | null;
  /** O scope item associado não é mais "Ativar": o objeto volta a contar sozinho. */
  dropped: boolean;
}

/** As cores que o WRICEF passa ao scope item associado (§3.3). */
const LENDS_TO_SCOPE_ITEM: ReadonlySet<AdherenceStatus> = new Set(["extension", "gap", "undecided"]);

function statusOf(
  decision: WricefDecisionUpdate["decision"],
  outOfScopeReason: WricefDecisionUpdate["out_of_scope_reason"],
  hasGap: boolean,
): AdherenceStatus {
  if (decision == null || decision === "client_validation") return "undecided";
  if (decision === "out_of_scope") {
    if (outOfScopeReason == null) return "undecided";
    return outOfScopeReason === "no_fit" ? "gap" : "excluded";
  }
  // O amarelo é "conversão e nenhum GAP" (§3.2): GAP do objeto deixa vermelho só
  // a conversão. O "Atendido por scope item standard" continua verde.
  if (decision === "in_scope") return hasGap ? "gap" : "extension";
  return "standard";
}

export function wricefAdherenceEffect({
  decision,
  outOfScopeReason,
  relatedScopeItem,
  hasGap,
  activeScopeItems,
}: {
  decision: WricefDecisionUpdate["decision"];
  outOfScopeReason: WricefDecisionUpdate["out_of_scope_reason"];
  relatedScopeItem: string | null;
  hasGap: boolean;
  /** Os scope items "Ativar"; sem a lista ainda, a associação é tida como válida. */
  activeScopeItems: ReadonlySet<string> | undefined;
}): WricefAdherenceEffect {
  const status = statusOf(decision, outOfScopeReason, hasGap);
  if (status === "excluded") {
    return { status, countsIn: "excluded", scopeItem: null, dropped: false };
  }
  if (!relatedScopeItem || !LENDS_TO_SCOPE_ITEM.has(status)) {
    return { status, countsIn: "developments", scopeItem: null, dropped: false };
  }
  const active = activeScopeItems === undefined || activeScopeItems.has(relatedScopeItem);
  return {
    status,
    countsIn: active ? "scope_item" : "developments",
    scopeItem: relatedScopeItem,
    dropped: !active,
  };
}
